import React, { useEffect, useMemo, useState } from "react";
import {
  IonModal,
  IonContent,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
} from "@ionic/react";
import { useDispatch, useSelector } from "react-redux";

import {
  getUserInfo,
  resetUserInfoThrottle,
} from "../../redux/actions/userActions";
import { sendRealEmailOtp, setVerifiedRealEmail } from "../../services/accountEmail";
import { toErrorText } from "../../services/errorText";
import "./RealEmailGate.css";
import { getAuthToken } from "../../services/authStorage";

const OTP_LEN = 4;

const isEmailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || "").trim());

/**
 * Blocking, non-dismissible gate shown after a subscription purchase (Stripe or
 * IAP) to any active subscriber whose login email is an Apple "Hide My Email"
 * relay address. It forces the user to enter and OTP-verify a real, deliverable
 * email so they can receive the Digital Magazine and subscription communications.
 *
 * Driven entirely by the backend flag `master_club_subscriber.needs_real_email`:
 * a purchase refreshes the profile → flag flips true → this opens. Verifying the
 * email clears the flag server-side → next profile refresh dismisses it. Because
 * it is flag-driven (not one-shot), it also catches existing relay subscribers on
 * the next app open and web purchases where no in-app success callback fires.
 */
const RealEmailGate = () => {
  const dispatch = useDispatch();

  const needsRealEmail = useSelector(
    (s) => s.user?.master_club_subscriber?.needs_real_email === true,
  );
  const hasBearer = useSelector((s) => !!s.user?.adoc_t) ||
    !!getAuthToken();

  // Only ever show while we have an authenticated session AND the flag is set.
  const open = needsRealEmail && hasBearer;

  const [step, setStep] = useState("email"); // "email" | "otp"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset internal state whenever the gate (re)opens.
  useEffect(() => {
    if (open) {
      setStep("email");
      setEmail("");
      setOtp("");
      setError("");
      setLoading(false);
    }
  }, [open]);

  const emailTrimmed = useMemo(() => (email || "").trim(), [email]);

  const requestOtp = async () => {
    if (!isEmailValid(emailTrimmed) || loading) return;
    setError("");
    setLoading(true);
    try {
      await sendRealEmailOtp(emailTrimmed);
      setOtp("");
      setStep("otp");
    } catch (e) {
      setError(toErrorText(e, "Errore durante l'invio del codice."));
    } finally {
      setLoading(false);
    }
  };

  const confirmOtp = async () => {
    if (otp.trim().length < OTP_LEN || loading) return;
    setError("");
    setLoading(true);
    try {
      await setVerifiedRealEmail(emailTrimmed, otp.trim());
      // Flag clears server-side — pull the fresh profile so the gate closes.
      resetUserInfoThrottle();
      await dispatch(getUserInfo())
        .unwrap()
        .catch(() => {});
    } catch (e) {
      setError(toErrorText(e, "Codice non valido o scaduto."));
      setLoading(false);
    }
    // On success we intentionally leave `loading` true: the gate unmounts as
    // soon as the refreshed profile clears the flag.
  };

  return (
    <IonModal
      isOpen={open}
      backdropDismiss={false}
      className="real-email-gate"
    >
      <IonContent className="ion-padding real-email-gate__content">
        <div className="real-email-gate__inner">
          <h2 className="real-email-gate__title">Conferma la tua email</h2>

          {step === "email" ? (
            <>
              <IonText className="real-email-gate__subtitle">
                Per ricevere il Magazine Digitale e le comunicazioni relative al
                tuo abbonamento, inserisci un indirizzo email valido.
              </IonText>

              <div className="real-email-gate__field">
                <label className="real-email-gate__label">Email</label>
                <IonInput
                  value={email}
                  onIonInput={(e) => setEmail(e.detail?.value ?? "")}
                  type="email"
                  inputMode="email"
                  autocapitalize="off"
                  placeholder="latua@email.com"
                  className="real-email-gate__input"
                />
              </div>

              {!!error && (
                <IonText color="danger" className="real-email-gate__error">
                  {error}
                </IonText>
              )}

              <IonButton
                expand="block"
                shape="round"
                disabled={!isEmailValid(emailTrimmed) || loading}
                onClick={requestOtp}
              >
                {loading ? <IonSpinner name="dots" /> : "Invia codice"}
              </IonButton>
            </>
          ) : (
            <>
              <IonText className="real-email-gate__subtitle">
                Inserisci il codice di verifica che abbiamo inviato a{" "}
                <strong>{emailTrimmed}</strong>.
              </IonText>

              <div className="real-email-gate__field">
                <label className="real-email-gate__label">Codice</label>
                <IonInput
                  value={otp}
                  onIonInput={(e) => setOtp(e.detail?.value ?? "")}
                  inputMode="numeric"
                  type="tel"
                  maxlength={8}
                  placeholder="––––"
                  className="real-email-gate__input real-email-gate__input--otp"
                  autofocus
                />
              </div>

              {!!error && (
                <IonText color="danger" className="real-email-gate__error">
                  {error}
                </IonText>
              )}

              <IonButton
                expand="block"
                shape="round"
                disabled={otp.trim().length < OTP_LEN || loading}
                onClick={confirmOtp}
              >
                {loading ? <IonSpinner name="dots" /> : "Verifica"}
              </IonButton>

              <IonButton
                expand="block"
                fill="clear"
                disabled={loading}
                onClick={() => {
                  setError("");
                  setStep("email");
                }}
              >
                Cambia email
              </IonButton>
            </>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default RealEmailGate;
